import React from 'react';

// ── SVG icons ─────────────────────────────────────────────────────────────────

const Spark = ({ size = 14, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true">
    <path d="M12 2 L13.8 8.2 L20 10 L13.8 11.8 L12 18 L10.2 11.8 L4 10 L10.2 8.2 Z" />
    <path d="M19 14 L19.7 16.3 L22 17 L19.7 17.7 L19 20 L18.3 17.7 L16 17 L18.3 16.3 Z" opacity=".75" />
  </svg>
);

const Check = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const Lock = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);

// ── Tiny helpers ──────────────────────────────────────────────────────────────

const Dot = ({ color }) => (
  <span style={{
    display: 'inline-block', width: 7, height: 7, borderRadius: 999,
    background: color, boxShadow: `0 0 8px ${color}`, flexShrink: 0,
  }} />
);

function MonoAvatar({ initials = '', hue = 250, size = 36 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: 999,
      background: `linear-gradient(135deg, hsl(${hue} 38% 32%), hsl(${hue} 28% 18%))`,
      color: 'rgba(255,255,255,0.85)',
      fontSize: size * 0.36, fontWeight: 700,
      display: 'grid', placeItems: 'center',
      border: '2px solid rgba(255,255,255,0.06)',
      flexShrink: 0,
    }}>{initials}</div>
  );
}

// ── Chat bubbles ──────────────────────────────────────────────────────────────

function FanBubble({ name, hue, children }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', maxWidth: '78%' }}>
      <MonoAvatar initials={name[0]} hue={hue} size={32} />
      <div style={{
        background: 'var(--aai-bubble)',
        border: '1px solid rgba(255,255,255,0.05)',
        padding: '11px 15px 12px',
        borderRadius: '18px 18px 18px 4px',
        fontSize: 14.5, lineHeight: 1.45,
        color: 'rgba(255,255,255,0.92)',
        boxShadow: '0 1px 0 rgba(255,255,255,0.04) inset, 0 6px 18px rgba(0,0,0,0.35)',
      }}>{children}</div>
    </div>
  );
}

function AiBubble({ children, attachment }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, marginLeft: 'auto', maxWidth: '82%' }}>
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
        color: 'var(--aai-ai)',
      }}>
        <Spark size={11} color="var(--aai-ai)" />
        <span>Premsi AI</span>
      </div>
      <div style={{
        background: 'linear-gradient(180deg, var(--aai-soft), rgba(139,107,255,0.05))',
        border: '1px solid var(--aai-stroke)',
        padding: '11px 15px 12px',
        borderRadius: '18px 18px 4px 18px',
        fontSize: 14.5, lineHeight: 1.45, color: '#fff',
        boxShadow: '0 1px 0 rgba(255,255,255,0.08) inset, 0 8px 28px var(--aai-soft), 0 0 0 1px rgba(0,0,0,0.2)',
        backdropFilter: 'blur(8px)',
      }}>{children}</div>
      {attachment}
    </div>
  );
}

function LockedOffer({ title, price }) {
  return (
    <div style={{
      width: '100%', maxWidth: 280,
      background: 'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 18, overflow: 'hidden',
      boxShadow: '0 12px 32px rgba(0,0,0,0.45)',
    }}>
      <div style={{
        height: 88,
        background: 'radial-gradient(120% 100% at 30% 20%, var(--aai-soft), transparent 60%), linear-gradient(135deg, #2b1d4d, #1a1326)',
        position: 'relative', display: 'grid', placeItems: 'center',
      }}>
        <div style={{
          width: 38, height: 38, borderRadius: 999,
          border: '1.5px solid rgba(255,255,255,0.7)',
          display: 'grid', placeItems: 'center',
          color: '#fff', background: 'rgba(0,0,0,0.25)',
        }}><Lock size={16} /></div>
        <div style={{
          position: 'absolute', inset: 0,
          backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
          pointerEvents: 'none',
        }} />
      </div>
      <div style={{ padding: '12px 14px 14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: '#fff' }}>{title}</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginTop: 1 }}>{price} · sent by AI</div>
          </div>
          <button style={{
            border: 0, color: '#fff', fontWeight: 700, fontSize: 12.5,
            padding: '7px 14px', borderRadius: 999, cursor: 'default',
            background: 'linear-gradient(180deg, var(--aai-ai), var(--aai-ai2))',
            boxShadow: '0 6px 18px var(--aai-soft), 0 0 0 1px rgba(255,255,255,0.08) inset',
            fontFamily: 'inherit',
          }}>Unlock</button>
        </div>
      </div>
    </div>
  );
}

// ── Callout tag ───────────────────────────────────────────────────────────────

function Callout({ label, dir = 'down', style: extraStyle }) {
  return (
    <div className="aai-callout" style={{
      position: 'absolute',
      ...extraStyle,
    }}>
      <div style={{
        padding: '8px 16px',
        background: 'rgba(20,20,26,0.85)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 12, fontSize: 13, fontWeight: 600,
        fontStyle: 'italic', color: '#fff',
        boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
        whiteSpace: 'nowrap',
      }}>{label}</div>
      <div style={{
        width: 10, height: 10,
        background: 'rgba(20,20,26,0.85)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderTop: 0, borderLeft: 0,
        transform: dir === 'down'
          ? 'rotate(45deg) translateY(-6px)'
          : 'rotate(-135deg) translateY(-6px)',
      }} />
    </div>
  );
}

// ── Chat card ─────────────────────────────────────────────────────────────────

function ChatCard() {
  return (
    <div style={{
      width: '100%', maxWidth: 620,
      padding: '26px 28px 30px',
      borderRadius: 28,
      background: 'linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02) 40%, rgba(255,255,255,0.01))',
      border: '1px solid rgba(255,255,255,0.07)',
      boxShadow: '0 1px 0 rgba(255,255,255,0.06) inset, 0 40px 100px rgba(0,0,0,0.55), 0 0 0 1px rgba(0,0,0,0.4)',
      backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
      position: 'relative',
    }}>
      {/* header row */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        paddingBottom: 18, marginBottom: 18,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 999, flexShrink: 0,
            background: 'linear-gradient(135deg, #2a2a32, #444454)',
            border: '2px solid rgba(255,255,255,0.15)',
            boxShadow: '0 0 0 1px rgba(0,0,0,0.5), 0 0 12px rgba(255,255,255,0.15)',
          }} />
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 15, fontWeight: 700, color: '#fff' }}>
              <span>sarachad</span>
              <span style={{ color: 'var(--aai-ai)', display: 'inline-flex' }}><Check /></span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'rgba(255,255,255,0.55)', marginTop: 2 }}>
              <Dot color="#34C759" />
              <span>AI auto-reply active</span>
            </div>
          </div>
        </div>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 7,
          padding: '6px 10px',
          background: 'var(--aai-soft)',
          border: '1px solid var(--aai-stroke)',
          borderRadius: 999,
          fontSize: 11.5, fontWeight: 700, letterSpacing: '0.04em',
          color: 'var(--aai-ai)', flexShrink: 0,
        }}>
          <Spark size={11} color="var(--aai-ai)" />
          <span>Replying instantly</span>
        </div>
      </div>

      {/* messages */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <FanBubble name="L" hue={20}>
          I seriously love the recipes you share, especially the quick high-protein meals 😋
        </FanBubble>
        <AiBubble>
          Thank youu, that's amazing ❤️ Are you more into quick meal‑prep, low‑cal dinners, or sweet high‑protein snacks?
        </AiBubble>
        <FanBubble name="L" hue={20}>
          Mostly quick meals. I don't have much time but I want stuff that's healthy and easy.
        </FanBubble>
        <AiBubble attachment={<LockedOffer title="Fast & Healthy Recipes ❤️" price="$5" />}>
          Nicee, I actually have an exclusive <i>Fast Recipes</i> bundle — weekly meals, shopping lists, simple instructions. Sounds made for you 😊
        </AiBubble>
      </div>

      {/* composer */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        marginTop: 22, padding: '10px 12px',
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 999,
      }}>
        <div style={{
          width: 26, height: 26, flexShrink: 0, borderRadius: 999,
          display: 'grid', placeItems: 'center',
          background: 'var(--aai-soft)', border: '1px solid var(--aai-stroke)',
        }}><Spark size={12} color="var(--aai-ai)" /></div>
        <div style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.45)', flex: 1 }}>
          Premsi AI is typing<span style={{ letterSpacing: 2, marginLeft: 2 }}>···</span>
        </div>
        <div style={{
          fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.55)',
          padding: '4px 10px', borderRadius: 999, flexShrink: 0,
          background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.06)',
        }}>0.8s avg</div>
      </div>
    </div>
  );
}

// ── Settings card ─────────────────────────────────────────────────────────────

function MiniToggle({ on }) {
  return (
    <div style={{
      width: 36, height: 22, borderRadius: 999, flexShrink: 0,
      background: on ? 'var(--aai-ai)' : 'rgba(255,255,255,0.12)',
      position: 'relative',
    }}>
      <div style={{
        position: 'absolute', top: 2, left: on ? 16 : 2,
        width: 18, height: 18, borderRadius: 999,
        background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      }} />
    </div>
  );
}

function Segmented({ options, active }) {
  return (
    <div style={{ display: 'flex', padding: 2, background: 'rgba(255,255,255,0.08)', borderRadius: 8 }}>
      {options.map((o, i) => (
        <div key={o} style={{
          padding: '5px 12px', fontSize: 11.5, fontWeight: 700, borderRadius: 6,
          background: i === active ? 'var(--aai-ai)' : 'transparent',
          color: i === active ? '#fff' : 'rgba(255,255,255,0.55)',
        }}>{o}</div>
      ))}
    </div>
  );
}

function SRow({ label, children }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0' }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{label}</div>
      {children}
    </div>
  );
}

function SettingsCard() {
  const modes = ['Friendly',  'Pro', 'Hype'];
  return (
    <div style={{
      width: 320, borderRadius: 22,
      background: 'rgba(14,14,18,0.85)',
      border: '1px solid rgba(255,255,255,0.08)',
      boxShadow: '0 30px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(0,0,0,0.4)',
      backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
      overflow: 'hidden',
    }}>
      {/* gradient header */}
      <div style={{
        padding: '16px 18px 18px',
        background: 'linear-gradient(135deg, var(--aai-ai2), var(--aai-ai))',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12, flexShrink: 0,
            background: 'rgba(255,255,255,0.18)',
            display: 'grid', placeItems: 'center', color: '#fff',
            border: '1px solid rgba(255,255,255,0.25)',
          }}><Spark size={20} color="#fff" /></div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#fff' }}>Premsi AI</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'rgba(255,255,255,0.85)', marginTop: 2 }}>
              <Dot color="#34C759" />
              <span>Active · auto-replying</span>
            </div>
          </div>
          {/* on-toggle */}
          <div style={{
            width: 40, height: 24, borderRadius: 999, flexShrink: 0,
            background: '#34C759', position: 'relative',
            boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.1)',
          }}>
            <div style={{
              position: 'absolute', top: 2, left: 18,
              width: 20, height: 20, borderRadius: 999,
              background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            }} />
          </div>
        </div>
        {/* stats strip */}
        <div style={{
          marginTop: 14, padding: '10px 4px',
          background: 'rgba(255,255,255,0.12)',
          border: '1px solid rgba(255,255,255,0.16)',
          borderRadius: 12,
          display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
        }}>
          {[['142','Replies today'],['94%','Happy fans'],['$38','Earned today']].map(([n, l], i) => (
            <div key={l} style={{
              textAlign: 'center',
              borderLeft: i ? '1px solid rgba(255,255,255,0.18)' : 'none',
            }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', lineHeight: 1 }}>{n}</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.75)', marginTop: 4 }}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* personality body */}
      <div style={{ padding: '16px 18px 18px' }}>
        <div style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.14em',
          textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)', marginBottom: 12,
        }}>Personality</div>

        <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 8 }}>AI Mode</div>
        <div style={{
          display: 'flex', padding: 3, gap: 2,
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 10, marginBottom: 16,
        }}>
          {modes.map((m, i) => (
            <div key={m} style={{
              flex: 1, textAlign: 'center', padding: '7px 0',
              fontSize: 12, fontWeight: 700, borderRadius: 8,
              background: i === 0 ? 'var(--aai-ai)' : 'transparent',
              color: i === 0 ? '#fff' : 'rgba(255,255,255,0.55)',
              boxShadow: i === 0 ? '0 4px 14px var(--aai-soft)' : 'none',
            }}>{m}</div>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 8 }}>
          <span>Warmth</span>
          <span style={{ color: 'rgba(255,255,255,0.55)', fontWeight: 500 }}>Warm</span>
        </div>
        <div style={{ height: 6, borderRadius: 999, background: 'rgba(255,255,255,0.08)', position: 'relative', marginBottom: 18 }}>
          <div style={{
            position: 'absolute', left: 0, top: 0, bottom: 0, width: '62%',
            borderRadius: 999,
            background: 'linear-gradient(90deg, var(--aai-ai2), var(--aai-ai))',
          }} />
          <div style={{
            position: 'absolute', left: '62%', top: '50%',
            transform: 'translate(-50%,-50%)',
            width: 16, height: 16, borderRadius: 999,
            background: '#fff', boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
          }} />
        </div>

        <SRow label="Use emojis in replies"><MiniToggle on /></SRow>
        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />
        <SRow label="Sell my products"><MiniToggle on /></SRow>
        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />
        <SRow label="Language style"><Segmented options={['Casual','Formal']} active={0} /></SRow>
      </div>
    </div>
  );
}

// ── Stats badge ───────────────────────────────────────────────────────────────

function StatNum({ n, l }) {
  return (
    <div>
      <div style={{ fontSize: 16, fontWeight: 800, color: '#fff', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{n}</div>
      <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.55)', marginTop: 3, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{l}</div>
    </div>
  );
}

function StatBadge() {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 14,
      padding: '12px 18px 12px 14px',
      background: 'rgba(14,14,18,0.92)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 16,
      boxShadow: '0 20px 50px rgba(0,0,0,0.55)',
      backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
    }}>
      <div style={{
        width: 46, height: 46, borderRadius: 12, flexShrink: 0,
        background: 'linear-gradient(135deg, var(--aai-ai2), var(--aai-ai))',
        display: 'grid', placeItems: 'center',
        boxShadow: '0 8px 22px var(--aai-soft)',
      }}><Spark size={20} color="#fff" /></div>
      <div>
        <div style={{ fontSize: 14.5, fontWeight: 700, color: '#fff' }}>Today with AI</div>
        <div style={{ display: 'flex', gap: 14, marginTop: 6 }}>
          <StatNum n="142" l="replies" />
          <StatNum n="94%" l="happy fans" />
          <StatNum n="$38" l="earned" />
        </div>
      </div>
    </div>
  );
}

// ── Feature row ───────────────────────────────────────────────────────────────

const FEATURES = [
  {
    title: 'Trained on your voice',
    body:  'Premsi AI learns from your past chats, your offers, your tone — and stays in character.',
    icon:  <Spark size={18} color="var(--aai-ai)" />,
  },
  {
    title: 'Sells while you sleep',
    body:  'Recommends bundles, unlocks PPVs and closes tips automatically, with creator-set guardrails.',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--aai-ai)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
  },
  {
    title: "You're always in control",
    body:  'Switch tone, jump in mid-conversation, blacklist words, set price ranges. Take over with one tap.',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--aai-ai)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>
      </svg>
    ),
  },
];

function FeatureRow() {
  return (
    <div className="aai-features">
      {FEATURES.map(it => (
        <div key={it.title} className="aai-feature-card">
          <div className="aai-feature-icon">{it.icon}</div>
          <div className="aai-feature-title">{it.title}</div>
          <div className="aai-feature-body">{it.body}</div>
        </div>
      ))}
    </div>
  );
}

// ── Section ───────────────────────────────────────────────────────────────────

export default function AltAISection() {
  return (
    <section className="aai-section" aria-label="Premsi AI">
      {/* decorative grid lines */}
      <div className="aai-grid" aria-hidden="true" />

      {/* header */}
      <div className="aai-header">
        <div className="aai-kicker">
          <Spark size={12} color="var(--aai-ai)" />
          <span>Premsi AI · Direct Messages</span>
        </div>
        <h2 className="aai-headline">
          Your AI. Your Voice.{' '}
          <span className="aai-headline-accent">Always On.</span>
        </h2>
        <p className="aai-subhead">
          Premsi AI chats with your fans in your voice — recommends bundles, unlocks PPVs, closes tips.
          You set the personality. It does the typing.
        </p>
      </div>

      {/* composed stage */}
      <div className="aai-stage">
        <div className="aai-stage-top">
          <div className="aai-badge">
            <StatBadge />
          </div>

          <div className="aai-settings-wrap">
            <SettingsCard />
          </div>
        </div>

        <div className="aai-chat-wrap">
          <ChatCard />
        </div>

        {/* callout labels — hidden ≤1024px via .aai-callout in CSS */}
        <Callout
          label="Replies in your voice"
          style={{ left: 'calc(50% - 360px)', top: 380, transform: 'rotate(-2deg)' }}
        />
        <Callout
          label="Tuned by you"
          style={{ right: '5%', top: 540 }}
        />
      </div>

      {/* feature cards */}
      <FeatureRow />
    </section>
  );
}
